import React, { useRef, useState } from 'react'
import StyledDesktopDatePicker from '../UIComponents/Calendars/StyledDesktopDatePicker'
import SelectedFieldsCalendarLegend from './SelectedFieldsCalendarLegend'
import TemporalComparison from './TemporalComparison'
import styled from 'styled-components'
import { DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core'
import { groupBy, pickBy } from 'lodash'
import size from 'lodash/size'
import isEmpty from 'lodash/isEmpty'
import { MAX_CLOUD_COVERAGE, TRACK_EVENTS } from '../../constants'
import StyledSlider from '../UIComponents/StyledSlider'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import { trackEvent } from '../analytics/analytics'

// taken from: https://github.com/clauderic/dnd-kit/blob/master/packages/modifiers/src/utilities/restrictToBoundingRect.ts
// based on: https://docs.dndkit.com/api-documentation/modifiers#building-custom-modifiers
function restrictToBoundingRect(transform, rect, boundingRect) {
  const value = {
    ...transform
  }

  if (rect.top + transform.y <= boundingRect.top) {
    value.y = boundingRect.top - rect.top
  } else if (rect.bottom + transform.y >= boundingRect.top + boundingRect.height) {
    value.y = boundingRect.top + boundingRect.height - rect.bottom
  }

  if (rect.left + transform.x <= boundingRect.left) {
    value.x = boundingRect.left - rect.left
  } else if (rect.right + transform.x >= boundingRect.left + boundingRect.width) {
    value.x = boundingRect.left + boundingRect.width - rect.right
  }

  return value
}

const restrictToElement = ref => ({ draggingNodeRect, transform }) => {
  if (!draggingNodeRect || !ref || !ref.current) {
    return transform
  }

  const { current } = ref
  const boundingBox = current.getBoundingClientRect()

  return restrictToBoundingRect(transform, draggingNodeRect, boundingBox)
}

const PaddingWrapper = styled.div`
  padding: 0 30px 0 40px;
  margin-top: -20px;
`

const SliderWrapper = styled.div`
  padding: 5px 40px;
  margin-top: 10px;
  margin-bottom: 0px;
`

const labelStyle = {
  color: themeColors.whiteColor,
  fontSize: 12
}

function SelectedFieldsCalendar({
  isLoading,
  snapshots,
  activeDate,
  calendarDots,
  flightGroups,
  selectFields,
  cloudCoverage,
  setCloudCoverage,
  selectFlightGroup,
  hasRoleSHCalendar,
  hasRoleS2dr,
  tempComparisonDate,
  compareToFlightGroup,
  setActiveCalendarDate,
  activeDateFlightGroups,
  orderedFlightGroupDates,
  selectedFlightGroupDate,
  flightGroupInfoFilteredByDate,
  droneFlightsSnapshotsDates
}) {
  const [isComparable, setIsComparable] = useState(false)
  const [droneSnapshotsMode, setDroneSnapshotsMode] = useState(false)

  const hasDroneSnapshots = !isEmpty(droneFlightsSnapshotsDates)
  const handleSetDroneSnapshotsMode = mode => {
    if (hasDroneSnapshots) {
      setDroneSnapshotsMode(mode)
    }
  }

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10
    }
  })
  const sensors = useSensors(mouseSensor)
  const constrainingContainer = useRef()
  const restrict = restrictToElement(constrainingContainer)

  const onDragStart = ({ active }) => {
    const tempDateFlightGroups = active.data.current.currentDateFlightGroups
    const flightGroupsBySources = groupBy([...activeDateFlightGroups, ...tempDateFlightGroups], 'source')
    const availableFlightGroupsBySources = pickBy(flightGroupsBySources, flightGroups => size(flightGroups) === 2)
    setIsComparable(size(availableFlightGroupsBySources) > 0)
  }

  const onDragEnd = ({ over, active }) => {
    if (over && isComparable) compareToFlightGroup(active.id)
  }

  const handleBarChange = event => {
    setCloudCoverage(event)

    trackEvent(TRACK_EVENTS.FIELD_CLOUD_COVERAGE, {
      value: event,
      button: 'Cloud Coverage progress bar',
      location: TRACK_EVENTS.FIELD_PANEL
    })
  }

  return (
    <div ref={constrainingContainer}>
      {hasRoleSHCalendar ? null : (
        <SliderWrapper>
          <StyledSlider
            step={1}
            unit={'%'}
            minValue={0}
            fullWidth={true}
            labelStyle={labelStyle}
            label={'Cloud Coverage'}
            onChange={handleBarChange}
            defaultValue={cloudCoverage}
            maxValue={MAX_CLOUD_COVERAGE}
          />
        </SliderWrapper>
      )}

      <SelectedFieldsCalendarLegend
        hasDroneSnapshots={hasDroneSnapshots}
        hasRoleS2dr={hasRoleS2dr}
        droneSnapshotsMode={droneSnapshotsMode}
        handleSetDroneSnapshotsMode={handleSetDroneSnapshotsMode}
      />

      <DndContext sensors={sensors} modifiers={[restrict]} onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <StyledDesktopDatePicker
          snapshots={snapshots}
          isLoading={isLoading}
          activeDate={activeDate}
          selectFields={selectFields}
          flightGroups={flightGroups}
          calendarDots={calendarDots}
          cloudCoverage={cloudCoverage}
          selectFlightGroup={selectFlightGroup}
          setActiveDate={setActiveCalendarDate}
          droneSnapshotsMode={droneSnapshotsMode}
          orderedFlightGroupDates={orderedFlightGroupDates}
          selectedFlightGroupDate={selectedFlightGroupDate}
          droneFlightsSnapshotsDates={droneFlightsSnapshotsDates}
          flightGroupInfoFilteredByDate={flightGroupInfoFilteredByDate}
        />

        <PaddingWrapper>
          <TemporalComparison
            isComparable={isComparable}
            selectedFlightGroupDate={selectedFlightGroupDate}
            tempComparisonFlightGroupDate={tempComparisonDate}
          />
        </PaddingWrapper>
      </DndContext>
    </div>
  )
}

export default SelectedFieldsCalendar
